import type { GameData, Participant } from "../game/model.ts";
import type { InterpolatedWorld } from "./snapshot.ts";

/**
 * Writes a server world onto a local `GameData`.
 *
 * The renderer, camera, minimap and HUD all read `GameData`. Rather than teach them
 * a second shape for online play, the client keeps one `GameData` and overwrites it
 * from each interpolated snapshot. Objects are reused across frames so a 60fps
 * render loop does not allocate a new participant list every frame.
 *
 * Sparks are deliberately left alone: they are pure decoration driven by local
 * events, and the server does not send them.
 */
export function applySnapshot(game: GameData, world: InterpolatedWorld, localId: string) {
  game.timeLeft = world.timeLeft;
  game.elapsed = world.elapsed ?? game.elapsed;
  game.bounty = world.bounty ?? null;
  game.running = true;

  applyParticipants(game, world, localId);
  applyShots(game, world);
  applyEnergy(game, world);
}

/**
 * The client caches its energy array and only rebuilds it when a delta arrives, so
 * the reference is stable across frames. Rebuilding ~200 particles every frame was
 * pure waste; skipping on an unchanged reference makes it free.
 */
const lastEnergyRef = new WeakMap<GameData, unknown>();

/** Spreads neighbouring ids across the whole cycle instead of adjacent phases. */
function scatteredPhase(id: number) {
  const hashed = Math.imul(id, 2654435761) >>> 0;
  return (hashed % 10000) / 10000 * Math.PI * 2;
}

function applyParticipants(game: GameData, world: InterpolatedWorld, localId: string) {
  const existing = new Map(game.participants.map((participant) => [participant.id, participant]));
  const next: Participant[] = [];

  for (const incoming of world.players) {
    const participant = existing.get(incoming.i) ?? blankParticipant(incoming.i, incoming.i === localId);
    participant.name = incoming.n;
    participant.color = incoming.c;
    participant.x = incoming.x;
    participant.y = incoming.y;
    participant.vx = incoming.vx;
    participant.vy = incoming.vy;
    participant.orbs = incoming.o;
    participant.score = incoming.s;
    participant.alive = incoming.a === 1;
    // The wire carries flags, not timers: the client only needs to know whether to
    // draw the shield ring and the boost trail.
    participant.shield = incoming.sh === 1 ? 1 : 0;
    participant.boostRemaining = incoming.bo === 1 ? 1 : 0;
    participant.shieldCooldown = incoming.cd;
    participant.spawnProtection = incoming.sp === 1 ? 1 : 0;
    participant.surgeCooldown = incoming.sc;
    participant.kind = incoming.i === localId ? "human" : participant.kind;
    next.push(participant);
  }

  game.participants = next;
}

function blankParticipant(id: string, isLocal: boolean): Participant {
  return {
    id,
    kind: isLocal ? "human" : "bot",
    name: "",
    color: "#ffffff",
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    orbs: 0,
    score: 0,
    alive: true,
    attackCooldown: 0,
    shield: 0,
    shieldCooldown: 0,
    boostRemaining: 0,
    respawn: 0,
    surgeCooldown: 0,
    spawnProtection: 0,
    targetX: 0,
    targetY: 0,
  };
}

function applyShots(game: GameData, world: InterpolatedWorld) {
  const existing = new Map(game.shots.map((shot) => [shot.id, shot]));
  const next: GameData["shots"] = [];

  for (const incoming of world.shots) {
    const shot = existing.get(incoming.i) ?? {
      active: true,
      id: incoming.i,
      owner: incoming.o,
      x: 0,
      y: 0,
      vx: 0,
      vy: 0,
      life: 0,
      traveled: 0,
      returning: false,
      stolen: false,
      surge: 0,
      taken: 0,
      color: "#ffffff",
    };
    shot.active = true;
    shot.owner = incoming.o;
    shot.x = incoming.x;
    shot.y = incoming.y;
    shot.vx = incoming.vx;
    shot.vy = incoming.vy;
    shot.returning = incoming.r === 1;
    shot.stolen = incoming.st === 1;
    shot.surge = incoming.sg;
    shot.color = incoming.c;
    next.push(shot);
  }

  game.shots = next;
}

function applyEnergy(game: GameData, world: InterpolatedWorld) {
  if (lastEnergyRef.get(game) === world.energy) return;
  lastEnergyRef.set(game, world.energy);

  const existing = new Map(game.energy.map((item) => [item.id, item]));
  const next: GameData["energy"] = [];

  for (const incoming of world.energy) {
    const item = existing.get(incoming.i) ?? {
      id: incoming.i,
      owner: incoming.o,
      x: 0,
      y: 0,
      color: "#ffffff",
      size: 4,
      // Stable per id, but scattered: consecutive ids used to land on neighbouring
      // phases, so the eight particles a dying core drops in one spot pulsed in
      // unison and read as a blinking blob.
      phase: scatteredPhase(incoming.i),
    };
    item.owner = incoming.o;
    item.x = incoming.x;
    item.y = incoming.y;
    item.color = incoming.c;
    item.size = incoming.z;
    next.push(item);
  }

  game.energy = next;
}
